import { contractAddresses } from 'src/config/contract-loader';
import { axiosInstanceNew } from 'src/utils/axios';
import { handleContractError } from 'src/utils/crypto/handleContractError';

const MAX_CREATE_INTENT_ATTEMPTS = 3;

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function formatBpsToPercent(bps) {
  const num = Number(bps);
  if (!Number.isFinite(num)) return null;

  const pct = num / 100;
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(2)}%`;
}

function buildCreateSwapIntentError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};

  if (
    status === 409 &&
    (data?.code === 'QUOTE_CHANGED' || data?.error === 'Quote changed beyond slippage.')
  ) {
    const slippage = formatBpsToPercent(data?.slippageBps);
    const delta = formatBpsToPercent(data?.deltaBps);

    if (slippage && delta) {
      return `Quote changed by ${delta}, which is above your slippage limit of ${slippage}. Please refresh and try again.`;
    }

    return 'Quote changed beyond your allowed slippage. Please refresh and try again.';
  }

  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data?.error === 'string' && data.error.trim()) {
    return data.error;
  }

  return handleContractError(err);
}

function isSuccessStatus(status) {
  return status >= 200 && status < 300;
}

function normaliseTokenKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function isFiatLikeToken(value) {
  const key = normaliseTokenKey(value);
  return key === 'dollar' || key === 'usd';
}

function getTokenDecimals(token, tokenKey) {
  const decimals = Number(token?.decimals);

  if (Number.isInteger(decimals) && decimals >= 0) {
    return decimals;
  }

  const key = normaliseTokenKey(tokenKey);

  if (key === 'dollar' || key === 'usd' || key === 'tether' || key === 'usdt') {
    return 6;
  }

  if (key === 'ethereum' || key === 'eth' || key === 'weth') {
    return 18;
  }

  return 6;
}

function cleanDecimalValue(value, decimals = 6) {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  const str = String(value).trim();

  if (!str) return str;

  if (!/^-?\d+(\.\d+)?$/.test(str)) {
    return str;
  }

  const num = Number(str);

  if (!Number.isFinite(num)) {
    return str;
  }

  return num.toFixed(decimals).replace(/\.?0+$/, '');
}

function shouldRetryCreateIntent(err) {
  const status = err?.response?.status;

  if (!status) return true;
  if (status === 408 || status === 425 || status === 429) return true;
  if (status >= 500) return true;

  return false;
}

async function postSwapIntentWithRetry(body, attempt = 1) {
  try {
    return await axiosInstanceNew.post('/swaps/intent', body);
  } catch (err) {
    const canRetry = attempt < MAX_CREATE_INTENT_ATTEMPTS && shouldRetryCreateIntent(err);

    if (!canRetry) {
      throw err;
    }

    await delay(1_200 * attempt);
    return postSwapIntentWithRetry(body, attempt + 1);
  }
}

export const createSwapIntent = async (payload) => {
  try {
    const {
      intentId,
      txHashIn,
      blockNumber,

      amountIn,
      amountOut,

      destChain,
      sourceChainId,

      tokenOne,
      tokenTwo,
      tokenIn,
      tokenOut,

      slippage,

      address,
      from,
      to,
      recipientAddress,

      networkFee,
      phoneNumber,
      name,
      fiatDepositId,
      matchedLedgerId,
      fiatConfirmedAt,
    } = payload || {};

    const resolvedTokenIn = tokenIn || tokenOne?.coingeckoId || tokenOne?.ticker;
    const resolvedTokenOut = tokenOut || tokenTwo?.coingeckoId || tokenTwo?.ticker;

    if (!resolvedTokenIn) {
      throw new Error(`Missing tokenIn. tokenOne=${JSON.stringify(tokenOne)}`);
    }

    if (!resolvedTokenOut) {
      throw new Error(`Missing tokenOut. tokenTwo=${JSON.stringify(tokenTwo)}`);
    }

    const tokenInKey = normaliseTokenKey(resolvedTokenIn);
    const tokenOutKey = normaliseTokenKey(resolvedTokenOut);
    const isFiatDeposit = isFiatLikeToken(tokenInKey);

    if (!intentId && !isFiatDeposit) {
      throw new Error('Missing intentId before finalizing submitted swap intent.');
    }

    if (!txHashIn && !isFiatDeposit) {
      throw new Error('Missing txHashIn before finalizing submitted swap intent.');
    }

    const fromAddress = from || address || null;
    const bridgeAddress = to || contractAddresses?.FiatCryptoBridge || null;

    const amountInDecimals = getTokenDecimals(tokenOne, tokenInKey);
    const amountOutDecimals = getTokenDecimals(tokenTwo, tokenOutKey);

    const cleanedAmountIn = cleanDecimalValue(amountIn, amountInDecimals);
    const cleanedAmountOut = cleanDecimalValue(amountOut, amountOutDecimals);

    const cleanedNetworkFee = cleanDecimalValue(networkFee, 6);

    const body = {
      ...(intentId ? { intentId } : {}),
      ...(txHashIn ? { txHashIn } : {}),
      ...(blockNumber != null ? { blockNumber } : {}),

      tokenIn: tokenInKey,
      tokenOut: tokenOutKey,

      amountIn: cleanedAmountIn,
      amountOut: cleanedAmountOut,

      ...(bridgeAddress ? { to: bridgeAddress } : {}),
      ...(fromAddress ? { from: fromAddress } : {}),
      ...(address ? { address } : {}),
      ...(recipientAddress ? { recipientAddress } : {}),

      slippage,
      destChain,
      ...(sourceChainId != null ? { sourceChainId } : {}),

      networkFee: cleanedNetworkFee,
      ...(phoneNumber ? { phoneNumber } : {}),
      name,
      ...(fiatDepositId ? { fiatDepositId } : {}),
      ...(matchedLedgerId ? { matchedLedgerId } : {}),
      ...(fiatConfirmedAt ? { fiatConfirmedAt } : {}),
    };

    const res = await postSwapIntentWithRetry(body);

    if (!isSuccessStatus(res.status)) {
      throw new Error(`Server responded with ${res.status}`);
    }

    return {
      success: true,
      ok: res.data?.ok ?? true,

      id: res.data?.id,
      intentId: res.data?.intentId || intentId || null,

      txHashIn: res.data?.txHashIn || txHashIn || null,
      status: res.data?.status,

      amountInAtomic: res.data?.amountInAtomic,
      amountOutAtomic: res.data?.amountOutAtomic,

      bridgeAddress: res.data?.bridgeAddress,

      tokenIn: res.data?.tokenIn,
      tokenOut: res.data?.tokenOut,
      destChain: res.data?.destChain,

      serverQuote: res.data?.serverQuote,

      message: res.data?.message || 'Swap transaction is being tracked.',
    };
  } catch (err) {
    console.error('Failed to create/finalize swap intent:', err);

    const message = buildCreateSwapIntentError(err);

    return {
      success: false,
      ok: false,
      error: message,
      message,
      status: err?.response?.status ?? null,
      details: err?.response?.data ?? null,
      txHashIn: payload?.txHashIn || null,
      intentId: payload?.intentId || null,
    };
  }
};
